import type { IFieldDefinition, IStatusSet } from '@platform/core/types';
import {
  financeDots, optionDots, priorityDots, registryDots, severityDots, specClosedDot, specNegative, specNegativeDot, specOpenDots,
  statusDots, typePalettes,
} from './statusPalettes';

/** Крапка порожнього значення (прототип v4 dotFor: «немає значення» — колір лінії). */
const EMPTY_DOT = 'var(--line2)';
const FIRST_DOT = statusDots[0]!;

/** Тип за підписом набору статусів (для викликів без ключа типу): перший тип таблиці з таким підписом. */
const TYPE_BY_SIGNATURE = new Map<string, string>();
for (const [key, [, signature]] of Object.entries(typePalettes)) if (!TYPE_BY_SIGNATURE.has(signature)) TYPE_BY_SIGNATURE.set(signature, key);

/** Обчислені палітри: набір статусів → (ключ типу або '' → значення → колір). */
const palettes = new WeakMap<IStatusSet, Map<string, Record<string, string>>>();

const signatureOf = (statuses: IStatusSet): string => statuses.list.map((s) => `${s.value}${s.closed ? '*' : ''}`).join('|');

/** Тип власного модуля простору (ADR-025: ключ mod_…) — шаблон moduleTpl прототипу, крапки статусів за порядком (dotFor). */
const CUSTOM_TYPE = /^mod_[a-z0-9]+$/;

/**
 * Режим палітри типу (typePalettes): за ключем типу, без ключа — за підписом набору статусів; власний модуль — за порядком; решта типів —
 * правило специфікації.
 */
function modeOf(statuses: IStatusSet, recordType: string | undefined): string {
  if (recordType && CUSTOM_TYPE.test(recordType)) return 'index';
  const key = recordType ?? TYPE_BY_SIGNATURE.get(signatureOf(statuses));
  return (key && typePalettes[key]?.[0]) || 'spec';
}

/** Кольори всіх статусів набору в режимі `mode` (правила прототипу v4: shapeSpec, stripRegistry, shapeFinance, dotFor). */
function buildPalette(statuses: IStatusSet, mode: string): Record<string, string> {
  const out: Record<string, string> = {};
  let open = 0;
  statuses.list.forEach((s, i) => {
    const byIndex = statusDots[i % statusDots.length]!;
    if (s.color) out[s.value] = s.color;
    else if (mode === 'index') out[s.value] = byIndex;
    else if (mode === 'registry') out[s.value] = registryDots[s.value] ?? byIndex;
    else if (mode === 'finance') out[s.value] = financeDots[s.value] ?? byIndex;
    else if (specNegative.test(s.value)) out[s.value] = specNegativeDot;
    else if (s.closed && mode === 'spec') out[s.value] = specClosedDot;
    else out[s.value] = specOpenDots[open++ % specOpenDots.length]!;
  });
  return out;
}

function paletteOf(statuses: IStatusSet, recordType: string | undefined): Record<string, string> {
  let byType = palettes.get(statuses);
  if (!byType) palettes.set(statuses, (byType = new Map()));
  const key = recordType ?? '';
  let palette = byType.get(key);
  if (!palette) byType.set(key, (palette = buildPalette(statuses, modeOf(statuses, recordType))));
  return palette;
}

/**
 * Колір крапки статусу як у прототипі v4: колір статусу з метаданих → палітра типу (завдання, база знань — за порядком; реєстри Ради й фінанси —
 * за значенням; решта — правило специфікації модулів: незавершені по черзі, завершені — зелений, «негативні» — червоний). `recordType` — ключ
 * типу запису; без нього тип впізнається за набором статусів. Порожнє значення — колір лінії, невідоме — перший колір палітри.
 */
export function statusColor(statuses: IStatusSet, value: string | null | undefined, recordType?: string): string {
  if (!value) return EMPTY_DOT;
  return paletteOf(statuses, recordType)[value] ?? FIRST_DOT;
}

/**
 * Колір крапки варіанта поля-вибору (прототип v4 dotFor): колір варіанта з метаданих → власні кольори поля прототипу (посади членів комітетів) →
 * важливість і пріоритет за значенням → палітра за порядковим номером варіанта. Порожнє значення — колір лінії.
 */
export function optionColor(field: Pick<IFieldDefinition, 'key' | 'options'>, value: string | null | undefined, recordType?: string): string {
  if (!value) return EMPTY_DOT;
  const own = field.options?.find((o) => o.value === value)?.color;
  if (own) return own;
  const custom = recordType ? optionDots[`${recordType}.${field.key}`]?.[value] : undefined;
  if (custom) return custom;
  if (field.key === 'severity') return severityDots[value] ?? EMPTY_DOT;
  if (field.key === 'priority') return priorityDots[value] ?? FIRST_DOT;
  const index = field.options?.findIndex((o) => o.value === value) ?? -1;
  return statusDots[Math.max(0, index) % statusDots.length]!;
}

/** Чи поле має власні кольори варіантів (метадані або прототип): тоді список вибору показує крапки, як fieldItems прототипу v4. */
export function hasOptionColours(field: Pick<IFieldDefinition, 'key' | 'options'>, recordType?: string): boolean {
  return Boolean(field.options?.some((o) => o.color) || (recordType && optionDots[`${recordType}.${field.key}`]));
}

/** Колір крапки пріоритету або важливості за значенням варіанта (PRIO_DOTS / dotFor severity); невідоме значення — без крапки. */
export function priorityColor(value: string | null | undefined): string | undefined {
  return value ? priorityDots[value] ?? severityDots[value] : undefined;
}

export function tagColor(hue: number): string {
  return `oklch(0.66 0.14 ${hue})`;
}
